import { z } from 'zod';
import { myChangeSchema, quickItemSchema } from './quickAdd.js';
import { FOCUS_LABEL_MAX, focusItemSchema } from './noteFocus.js';
import { ICD10_CATALOG } from './icd10Catalog.js';
/**
 * Clinical Notes (#138) — progress notes + treatment plans. Bodies are clinical
 * PHI; the API JSON.stringify's the `body` object and AES-256-GCM encrypts it.
 * `status` is server-managed (draft on create; the submit/cosign/changes
 * endpoints move it), so it is never part of the create/update payloads.
 */
export const NOTE_FORMATS = ['SOAP', 'DAP', 'BIRP', 'CREDIFY'];
/** Display names ("Credify Note" is stored as CREDIFY). */
export const NOTE_FORMAT_LABELS = {
    SOAP: 'SOAP',
    DAP: 'DAP',
    BIRP: 'BIRP',
    CREDIFY: 'Credify Note',
};
/**
 * calendar#46 / PROGRESS_NOTE_A-15 — the Credify Note template's pick lists, verbatim
 * from the mockup. Shared so the individual and (later) group note editors, preview and
 * print all use the same vocabulary.
 */
export const CREDIFY_NOTE_OPTIONS = {
    concerns: [
        'Anxiety / worry',
        'Low mood',
        'Panic symptoms',
        'Sleep difficulty',
        'Anger / irritability',
        'Relationship conflict',
        'Substance use',
        'Trauma / flashbacks',
        'Grief / loss',
        'Work / school stress',
    ],
    interventions: [
        'Cognitive reframing',
        'Behavioral activation',
        'Breathing / relaxation technique',
        'Psychoeducation',
        'Role play / rehearsal',
        'Mindfulness exercise',
        'Problem-solving',
        'Motivational interviewing',
        'Exposure exercise',
        'Safety planning',
    ],
    response: [
        'Engaged and responsive',
        'Receptive, some resistance',
        'Guarded / withdrawn',
        'Distressed',
        'Minimal engagement',
        'Resistant',
    ],
    nextSteps: [
        'Continue current plan',
        'Assign homework / practice',
        'Increase session frequency',
        'Refer for medication evaluation',
        'Safety check-in next session',
        'Review treatment plan goals',
        'Coordinate with other provider',
    ],
};
// ehr#189: 'signed' = signed off by the clinician alone (Supervision off), final and locked.
export const CLINICAL_STATUSES = ['draft', 'pending', 'changes', 'cosigned', 'signed'];
const uuid = z.string().uuid();
const section = z.string().max(20000);
// ehr#437 (FAMILY_COUPLES_NOTE_10-14-V15): an attendee's attendance in a family or couples session.
export const ATTENDANCE_STATUSES = [
    'Attended',
    'Joined late',
    'Left early',
    'Not present',
];
/**
 * ehr#437 (FAMILY_COUPLES_NOTE_10-14-V15, 60 A / 71 A): Safety between partners. Three Yes/No
 * questions asked of each partner alone, kept and printed on that partner's note only (never sent
 * to NotePilot). Someone in the room who isn't a partner (a child, say) is marked Not a partner and
 * skipped. An unreviewed Yes keeps the session on Family; on Couples the check is needed to sign.
 */
export const PARTNER_SAFETY_QUESTIONS = [
    'Hit, kicked, punched or otherwise hurt by a partner in the past year?',
    'Feels unsafe in the current relationship?',
    'A past partner making them feel unsafe now?',
];
export const partnerSafetySchema = z.object({
    a: z
        .array(z.enum(['Yes', 'No']).nullable())
        .max(3)
        .optional(),
    notPartner: z.boolean().optional(),
    at: z.string().max(40).optional(),
    by: z.string().max(120).optional(),
    review: z
        .object({ by: z.string().max(120), at: z.string().max(40), text: z.string().min(1).max(300) })
        .optional(),
});
/** All three answered, or marked Not a partner. */
export const partnerSafetyDone = (p) => !!p &&
    (p.notPartner === true ||
        (Array.isArray(p.a) && p.a.length === 3 && p.a.every((v) => v === 'Yes' || v === 'No')));
/** A Yes nobody has marked reviewed yet. */
export const partnerSafetyOpen = (p) => !!p && !p.notPartner && Array.isArray(p.a) && p.a.includes('Yes') && !p.review;
/**
 * ehr#437 (GROUP_NOTE_10-14-V26, V73 101 A): each group member's Participation and Mood, required
 * on a group note (not for someone who wasn't there).
 */
export const GROUP_PARTICIPATION = ['Active', 'Some', 'Minimal', 'Did not take part'];
export const GROUP_MOODS = ['Stable', 'Anxious', 'Low', 'Irritable', 'Flat', 'Bright'];
/**
 * V73 (101 A): the other group members a note names (by first name, whole word). A group note never
 * names another member: it is part of this member's record.
 */
export function namedMembers(text, names) {
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return [...new Set(names.filter((n) => n.length > 1))].filter((n) => new RegExp(`\\b${esc(n)}\\b`).test(text));
}
// ehr#437 (V81): Safety, asked on every therapy note; "Concerns reported" opens the risk follow-up.
export const NOTE_PLACES = ['In person', 'Telehealth', 'Phone'];
export const SAFETY_STATUSES = [
    'Not assessed',
    'Assessed, no concerns',
    'Concerns reported',
];
export const RISK_LEVELS = ['Low', 'Moderate', 'High', 'Imminent'];
export const RISK_ACTIONS = [
    'Safety plan reviewed or updated',
    'Crisis line given',
    'Emergency contact notified',
    'Higher level of care arranged',
    '911 called',
];
export const noteSafetySchema = z.object({
    status: z.enum(SAFETY_STATUSES),
    level: z.enum(RISK_LEVELS).optional(),
    actions: z.array(z.enum(RISK_ACTIONS)).max(RISK_ACTIONS.length).optional(),
    notified: z.string().max(2000).optional(),
    followup: z.string().max(4000).optional(),
});
// ehr#437 (V81 INTERVENTION_ITEMS): the Interventions used checklist on SOAP and DAP notes.
export const INTERVENTION_ITEMS = [
    'CBT / cognitive restructuring',
    'DBT skills',
    'Motivational interviewing',
    'Psychoeducation',
    'Mindfulness / grounding',
    'Supportive therapy',
    'Safety planning',
    'Homework review',
];
// Session metadata shown in the note's session strip (time/type/duration).
export const noteSessionSchema = z.object({
    time: z.string().max(40).optional(), // "3:00 PM" (the start)
    type: z.string().max(60).optional(), // "Individual"
    durationMin: z.number().int().min(0).max(1440).optional(),
    // ehr#437 (PROGRESS_NOTE_10-14-V81 Session details): the end time, where it was held, and the
    // billing code the type and length make (shown on the note; RCM bills from the claim).
    end: z.string().max(40).optional(), // "3:50 PM"
    place: z.enum(NOTE_PLACES).optional(),
    code: z.string().max(40).optional(), // "90834", "90839 + 90840"
    // ehr#543: the billing line's modifier as the note shows it (the payor's rule from RCM, else 95
    // for Telehealth and 93 for Phone); the server sets it again from the place as it is signed.
    modifier: z.string().max(4).optional(),
    // ehr#478 (V81 Type, place and billing): a code picked by hand ('' is "No code"; left out, the
    // type and length pick it), interactive complexity (+90785) with its CMS reason, and for
    // telehealth and phone the client's state and where they were (POS 10 at home, 02 elsewhere).
    codePick: z.string().max(12).optional(),
    complexity: z.boolean().optional(),
    icReason: z.string().max(200).optional(),
    locState: z.string().max(40).optional(),
    clientAt: z.enum(['Home', 'Other']).optional(),
});
// One treatment-plan objective addressed in this session, snapshotted (text is
// copied in so the note stays accurate even if the plan later changes).
// ehr#571 (PROGRESS_NOTE_10-20-V141 STATUS_PICKER_A): how each goal and objective went this session.
export const GOAL_STATUSES = [
    'Not started',
    'In progress',
    'Partially met',
    'Met',
    'Not met',
    'Discontinued',
    'Not addressed this session',
];
const isoDay = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .or(z.literal(''));
// A status with its date (none on Not addressed), an optional note, and the plan's own status
// for the row as it was when the note was written ("Same as plan" / "Changed from").
const goalStatusFields = {
    status: z.enum(GOAL_STATUSES).optional(),
    statusDate: isoDay.optional(),
    note: z.string().max(2000).optional(),
    planStatus: z.string().max(60).optional(),
};
export const goalAddressedSchema = z.object({
    goalText: z.string().max(4000).default(''),
    objectiveText: z.string().max(4000).default(''),
    addressed: z.boolean().default(false),
    progress: z.string().max(40).optional(), // Improved | No change | Worsened (before ehr#571)
    ...goalStatusFields,
});
/** ehr#571: a goal's own status, for a goal that has objectives (they keep theirs on goalsAddressed). */
export const goalLevelStatusSchema = z.object({
    goalText: z.string().max(4000),
    ...goalStatusFields,
});
/** One diagnosis on a progress note (label snapshotted so the note reads the same later). */
export const noteDiagnosisSchema = z.object({
    code: z.string().min(1).max(12),
    label: z.string().max(200).default(''),
    source: z.enum(['tp', 'search']).optional(),
});
/**
 * The ICD-10 list every diagnosis picker offers. Since the ehr#741 follow-up this is the one catalog,
 * ICD10_CATALOG (767 billable codes, from the Treatment Plan module's library). The 38-code list
 * that came with calendar#46 / PROGRESS_NOTE_A-15 is retired: it still had F50.2 and F50.81, which
 * are no longer billable (now F50.20-F50.25 and F50.810-F50.819). A code saved before stays on its
 * note or authorization with the label it was saved with.
 * @deprecated Use ICD10_CATALOG. Kept so older imports keep working.
 */
export const ICD10_LIBRARY = ICD10_CATALOG;
/**
 * calendar#46 / PROGRESS_NOTE_A-15: which codes each Credify Note "Presenting concern" suggests in
 * the New Client note's Diagnosis codes card. Every code here is in ICD10_CATALOG (a test checks).
 */
export const DX_SUGGEST_MAP = {
    'Anxiety / worry': ['F41.1', 'F41.9', 'F40.10'],
    'Low mood': ['F32.9', 'F32.1', 'F33.9'],
    'Panic symptoms': ['F41.0', 'F41.9'],
    'Sleep difficulty': ['G47.00'],
    'Anger / irritability': ['F43.25'],
    'Relationship conflict': ['Z63.0'],
    'Substance use': ['F10.10', 'F11.20', 'F12.20'],
    'Trauma / flashbacks': ['F43.10', 'F43.0'],
    'Grief / loss': ['F43.21'],
    'Work / school stress': ['F43.0', 'F43.23'],
};
/** Credify NotePilot "Treatment approach" picker (calendar#46 / PROGRESS_NOTE_A-15, verbatim). */
export const TREATMENT_APPROACHES = [
    'Cognitive Behavioral Therapy (CBT)',
    'Dialectical Behavior Therapy (DBT)',
    'Acceptance and Commitment Therapy (ACT)',
    'Eye Movement Desensitization and Reprocessing (EMDR)',
    'Motivational Interviewing (MI)',
    'Solution-Focused Brief Therapy (SFBT)',
    'Psychodynamic Therapy',
    'Person-Centered Therapy',
    'Exposure and Response Prevention (ERP)',
    'Trauma-Focused CBT (TF-CBT)',
    'Interpersonal Therapy (IPT)',
    'Mindfulness-Based Cognitive Therapy (MBCT)',
    'Family Systems Therapy',
    'Narrative Therapy',
    'Play Therapy',
    'Behavioral Activation',
    'Cognitive Processing Therapy (CPT)',
    'Prolonged Exposure (PE)',
    'Gottman Method Couples Therapy',
    'Emotionally Focused Therapy (EFT)',
];
/** Session summary cap, in characters of text (the stored HTML is a little longer). */
export const NOTEPILOT_SUMMARY_MAX = 10000;
/** The note sections NotePilot can draft into (body keys). */
export const NOTEPILOT_FIELDS = [
    'subjective',
    'objective',
    'assessment',
    'plan',
    'data',
    'behavior',
    'intervention',
    'response',
];
// ehr#277 - a reviewer's suggested edits and comments on a returned note ("tracked changes"):
// the full suggested text of each section they changed, and comments tied to a section. The
// author accepts them (the suggested text replaces theirs) or rejects them; submitting the
// note clears any left over.
// ehr#571 (reopen 10-27): any box of the note's layout, not only the SOAP / DAP / BIRP ones: a New
// Client note's Presenting problem and Relevant history, a Quick add box (body.extras by id). The
// old keys still fit, so suggestions stored before read the same; the server keeps only the keys
// the note's own layout has (reviewSuggestions).
const noteSectionKey = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,47}$/);
export const noteSuggestionCommentSchema = z.object({
    section: noteSectionKey,
    text: z.string().trim().min(1).max(2000),
    at: z.string().max(40).optional(),
    // ehr#571 (reopen 10-24, V141 V92): a comment on words the reviewer selected in the box: the
    // words, and where they started in the box's text then (a hint, for words that appear twice).
    // Both optional: a comment without them is on the whole section, as every comment was before.
    quote: z.string().trim().min(1).max(500).optional(),
    pos: z.number().int().min(0).max(100000).optional(),
});
export const noteSuggestionsSchema = z.object({
    round: z.number().int().min(0).max(1000),
    /** The reviewer's user id. */
    by: z.string().max(64),
    at: z.string().max(40),
    sections: z.record(noteSectionKey, section).default({}),
    comments: z.array(noteSuggestionCommentSchema).max(50).default([]),
});
/**
 * ehr#571 (reopen 10-27, PROGRESS-NOTE V141 V94 / V96 / V101): after the first submission, a change
 * to a field that isn't a text box (date, times, length, session type, where, linked plan, billing
 * code, diagnosis, interventions, safety, goal statuses, checklists, ratings) by the reviewer or by
 * the author leaves a card beside the field: "Changed Start time from 9:02 AM to 10:04 AM", with an
 * optional comment. One entry per field and side: the key is "s:<field>" for the reviewer's change
 * and "a:<field>" for the author's, so the two never collide; a later round updates "to" and keeps
 * the first "from". Kept in the note body (body.fieldChanges), beside body.suggestions, because
 * suggestions are cleared on every resubmit and these stay as the note's record.
 */
export const noteFieldChangeSchema = z.object({
    key: z.string().regex(/^[sa]:[A-Za-z0-9_.:#-]{1,96}$/),
    label: z.string().trim().min(1).max(120),
    /** The note part the card sits beside (a block id: session, dx, goals, mentalStatus, …). */
    sec: z.string().max(48),
    from: z.string().max(1000),
    to: z.string().max(1000),
    /** Who made it (a user id; the server stamps it). */
    by: z.string().max(64),
    at: z.string().max(40),
    round: z.number().int().min(0).max(1000),
    comment: z.string().trim().max(2000).optional(),
    /**
     * ehr#741 (V141 V100): the field's value before the change, as JSON, so the red X on the card can
     * put it back. Cards saved before have none (their X says to change the field by hand).
     */
    fromRaw: z.string().max(8000).optional(),
});
export const noteFieldChangesSchema = z.array(noteFieldChangeSchema).max(100);
/**
 * ehr#741 (Chase 10-30, points 1C and 6; PROGRESS-NOTE V141 V100): a reply on a review card. A
 * reply typed and not yet saved has an empty `at`; the server stamps who and when. `id` is the
 * editor's own, so a reply sent twice (an autosave, then Save Draft) is kept once.
 */
export const cardReplySchema = z.object({
    id: z.string().max(40).optional(),
    by: z.string().max(64),
    at: z.string().max(40),
    text: z.string().trim().min(1).max(2000),
});
/**
 * ehr#741: a review card's conversation, kept on the note across rounds (body.reviewThreads): a
 * reviewer comment ("c:<round>.<n>", made as the note is sent back, with its text) or a
 * field-change card's replies ("fc:<card key>"), and whether either side ticked it (resolved).
 */
export const reviewThreadSchema = z.object({
    id: z.string().regex(/^(c:\d{1,4}\.\d{1,3}|fc:[sa]:[A-Za-z0-9_.:#-]{1,96})$/),
    section: z.string().max(48),
    text: z.string().max(2000).optional(),
    quote: z.string().max(500).optional(),
    pos: z.number().int().min(0).max(100000).optional(),
    by: z.string().max(64),
    at: z.string().max(40),
    round: z.number().int().min(0).max(1000),
    replies: z.array(cardReplySchema).max(50).default([]),
    resolved: z.boolean().optional(),
    /**
     * ehr#741 (Chase 10-31, points 6 and 8; V132): a field-change card turned down with its red X.
     * The card shows red and stays. Never set together with `resolved`.
     */
    denied: z.boolean().optional(),
    /**
     * ehr#741 (Chase 10-31, point 6): the person's own field-change card closed with its red X (and
     * a card that went with it, the Duration of an End time). It keeps no replies and no tick and
     * counts as never made, so a later change to the field starts a new card.
     */
    closed: z.boolean().optional(),
});
export const reviewThreadsSchema = z.array(reviewThreadSchema).max(200);
/**
 * ehr#682 (PROGRESS-NOTE V141 V86 / V87): the note's Comment history: every comment action, with
 * its text, kept in the note body (body.reviewLog; the reviewer's unsent ones in their review
 * draft until they send the note back or co-sign). The editor adds an entry with an empty `at` as
 * it happens (posted, replied, resolved, reopened, deleted, an edit kept or reverted, a field put
 * back); the server stamps who and when (reviewLog.ts). Never printed, never sent to the portal.
 */
export const REVIEW_LOG_KINDS = [
    'comment',
    'reply',
    'resolve',
    'reopen',
    'delete',
    'keep',
    'revert',
];
export const reviewLogEntrySchema = z.object({
    /** The editor's own id, so an entry sent twice (an autosave, then Save Draft) is kept once. */
    id: z.string().min(1).max(40),
    kind: z.enum(REVIEW_LOG_KINDS),
    /** The note part it is on, as the editor named it then (Plan, Session details, General). */
    section: z.string().max(48).optional(),
    /** The comment's or reply's words, or what the edit changed. */
    text: z.string().max(2000).optional(),
    by: z.string().max(64),
    at: z.string().max(40),
    round: z.number().int().min(0).max(1000),
});
/** The most entries a note keeps (the oldest go first). */
export const REVIEW_LOG_MAX = 500;
export const reviewLogSchema = z.array(reviewLogEntrySchema).max(REVIEW_LOG_MAX);
/**
 * ehr#682 (PROGRESS-NOTE V141 V83.4): after Request changes, the reviewer who sent the note back
 * may take it back into review for this long, until its author saves, replies or resubmits.
 */
export const TAKE_BACK_MS = 5 * 60 * 1000;
// Progress-note body: the SOAP/DAP/BIRP section keys (all optional; `format`
// decides which the editor/preview shows) plus the linked-plan context.
export const progressBodySchema = z.object({
    subjective: section.optional(),
    objective: section.optional(),
    assessment: section.optional(),
    plan: section.optional(),
    data: section.optional(),
    behavior: section.optional(),
    intervention: section.optional(),
    response: section.optional(),
    // #138 - linked treatment plan + the goals/objectives addressed this session.
    linkedPlanId: z.string().uuid().nullable().optional(),
    // ehr#708 (Jay 10-30, Q2): a discharge summary with no plan: why ("No treatment plan on file")
    noPlanReason: z.string().trim().max(80).optional(),
    session: noteSessionSchema.optional(),
    goalsAddressed: z.array(goalAddressedSchema).max(50).optional(),
    // ehr#571 (V141): the status of each goal that has objectives (its objectives' are above).
    goalStatuses: z.array(goalLevelStatusSchema).max(50).optional(),
    // ehr#682 (V141): Problems, goals, and objectives addressed: the chips picked (words copied in)
    // and the ones taken off this note only. Notes saved before kept a tick per goal row instead.
    focusAddressed: z.array(focusItemSchema).max(100).optional(),
    focusOff: z.array(z.string().max(FOCUS_LABEL_MAX)).max(200).optional(),
    // calendar#46 - Credify Note template (format CREDIFY): chip picks + "Other" free
    // text, the client-response pick, and next steps. Objective/Assessment reuse the
    // SOAP keys above.
    concerns: z.array(z.string().max(120)).max(30).optional(),
    concernsOther: z.string().max(500).optional(),
    interventions: z.array(z.string().max(120)).max(30).optional(),
    interventionsOther: z.string().max(500).optional(),
    clientResponse: z.string().max(120).optional(),
    clientResponseOther: z.string().max(500).optional(),
    nextSteps: z.array(z.string().max(120)).max(30).optional(),
    nextStepsOther: z.string().max(500).optional(),
    // calendar#46 step 3 - Diagnosis codes (ICD-10)*, picked from the client's treatment
    // plan or the code search. Required to submit (enforced server-side).
    diagnoses: z.array(noteDiagnosisSchema).max(25).optional(),
    // calendar#46 step 5 - Credify NotePilot. The summary is the clinician's working
    // text (limited rich text: lists only) and is not part of the note itself.
    // npDrafted lists sections still holding an unedited NotePilot draft; submit is
    // refused until the clinician edits them.
    approaches: z.array(z.string().max(120)).max(30).optional(),
    sessionSummary: z.string().max(40000).optional(),
    npDrafted: z.array(z.enum(NOTEPILOT_FIELDS)).max(8).optional(),
    // calendar#46 step 7 - group notes: this attendee's attendance (minutes, arrived
    // late / left early), and the group summary as it stood when the note was submitted
    // (snapshotted by the server; before submit the live session summary is shown).
    attendance: z
        .object({
        minutes: z.number().int().min(0).max(600).optional(),
        arrived: z.string().max(20).optional(),
        left: z.string().max(20).optional(),
        // ehr#437 (FAMILY_COUPLES_NOTE): this attendee's attendance; the identified client Not present
        // bills family therapy without the client (90846)
        status: z.enum(ATTENDANCE_STATUSES).optional(),
    })
        .optional(),
    groupSummary: section.optional(),
    // ehr#437 (GROUP_NOTE V26): this member's Participation and Mood
    participation: z.enum(GROUP_PARTICIPATION).optional(),
    groupMood: z.enum(GROUP_MOODS).optional(),
    // ehr#437 (FAMILY_COUPLES_NOTE): Safety between partners, on this partner's note only.
    partnerSafety: partnerSafetySchema.optional(),
    // fbs#97 - "Copy to new note": the saved note this one was started from. Signing off
    // is refused while every text section is still word-for-word that note's.
    copiedFrom: z
        .object({ noteId: z.string().min(1).max(64), date: z.string().max(10).optional() })
        .optional(),
    // ehr#277 - the reviewer's suggestions while the note is back with its author.
    suggestions: noteSuggestionsSchema.optional(),
    // ehr#571 (reopen 10-27): the field-change cards (see noteFieldChangeSchema).
    fieldChanges: noteFieldChangesSchema.optional(),
    // ehr#741: the review cards' conversations (see reviewThreadSchema)
    reviewThreads: reviewThreadsSchema.optional(),
    // ehr#741 (point 8): the note's boxes as they were when it was sent back, so the author's own
    // changes since show under each box ("What <reviewer> will see"); dropped as it is resubmitted
    returnedTexts: z.record(noteSectionKey, section).optional(),
    // ehr#682 (V141 V86): the Comment history (see reviewLogEntrySchema); the server keeps its own
    reviewLog: reviewLogSchema.optional(),
    // ehr#682 (V141 V92 Show changes): the boxes as the note was first sent for review, so a signed
    // or co-signed note can show what changed in review; set by the server at the first submit
    reviewBase: z.record(noteSectionKey, section).optional(),
    // ehr#571 (PROGRESS_NOTE_10-20-V141): signed more than the practice's late-entry hours after the
    // session: how long after, and why.
    lateEntry: z
        .object({
        hours: z.number().int().min(0).max(100000),
        reason: z.string().trim().min(1).max(2000),
    })
        .optional(),
    // ehr#437 (PROGRESS_NOTE_10-14-V81): Interventions used (SOAP and DAP; Client response is
    // `response` above), Why treatment continues (under Assessment), and Safety.
    interventionsUsed: z.array(z.string().max(120)).max(30).optional(),
    necessity: section.optional(),
    safety: noteSafetySchema.optional(),
    // ehr#437 part 3: the kind of note it was started as ("Case management", "Crisis", …), which
    // picks its layout, and the layout's own parts (Presenting problem, Mental status, …) by id.
    noteType: z.string().max(40).optional(),
    // ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, 13a): the author's peer specialist credential on a Peer
    // support note, as it was when the note was saved (so a signed note keeps the one it was signed
    // with). Sign-off is refused without it.
    peerCredential: z.string().trim().max(40).optional(),
    // ehr#437 part 7 (V81 copied-text check): sentences copied forward into each box, with the
    // past note's date, so a sentence still word for word can be flagged.
    copied: z
        .record(z.string().max(48), z
        .array(z.object({
        from: z.string().max(10),
        sentences: z.array(z.string().max(2000)).max(100),
    }))
        .max(20))
        .optional(),
    // ehr#437 part 6 (V81 leftovers): answers to "Did you: …?" from the last plan's "Next session:"
    // lines, and the sentence each wrote into Plan (so a changed answer rewrites it).
    followUps: z
        .array(z.object({
        item: z.string().max(300),
        ans: z.enum(['yes', 'no']),
        why: z.string().max(300).optional(),
        text: z.string().max(700),
    }))
        .max(10)
        .optional(),
    extras: z
        .record(z.string().max(48), z.union([section, z.array(z.string().max(160)).max(30)]))
        .optional(),
    // ehr#437 (V81 Quick add): the sections this note was given from Quick add (a copy of each, in
    // the order added; answers in extras by id), the "on every note" ones taken off this note, and
    // the My changes card (what was added or removed, with what Undo puts back).
    quick: z.array(quickItemSchema).max(30).optional(),
    quickOff: z.array(z.string().max(32)).max(40).optional(),
    myChanges: z.array(myChangeSchema).max(50).optional(),
});
/** ehr#437 part 3: kinds of note signed without a diagnosis (V81 NO_CODE_TYPES). */
export const NOTE_KINDS_WITHOUT_DX = ['No-show / cancellation', 'Admin / phone'];
/**
 * ehr#571 (reopen 10-27, V141 V92): the parts of a note that aren't text boxes, which its reviewer
 * may change while it is with them (applied as they co-sign or send it back). Text boxes go as
 * suggestions instead, for the author to Keep or Revert.
 */
export const REVIEW_FIELD_KEYS = [
    'linkedPlanId',
    'session',
    'goalsAddressed',
    'goalStatuses',
    'focusAddressed',
    'focusOff',
    'diagnoses',
    'interventionsUsed',
    'safety',
    'partnerSafety',
    'attendance',
    'participation',
    'groupMood',
    'concerns',
    'interventions',
    'clientResponse',
    'nextSteps',
    'extras',
];
export const reviewFieldsSchema = z.object({
    date: z.coerce.date().optional(),
    sessionLabel: z.string().min(1).max(120).optional(),
    body: progressBodySchema
        .pick({
        linkedPlanId: true,
        session: true,
        goalsAddressed: true,
        goalStatuses: true,
        focusAddressed: true,
        focusOff: true,
        diagnoses: true,
        interventionsUsed: true,
        safety: true,
        partnerSafety: true,
        attendance: true,
        participation: true,
        groupMood: true,
        concerns: true,
        interventions: true,
        clientResponse: true,
        nextSteps: true,
        extras: true,
    })
        .default({}),
});
/**
 * calendar#46 step 6 - a calendar appointment occurrence, "<eventId>@<YYYY-MM-DD>" (the
 * calendar's RCM deep-link convention). A note written from an appointment keeps it.
 */
export const APPOINTMENT_KEY_RE = /^[A-Za-z0-9_.:-]{1,100}@\d{4}-\d{2}-\d{2}$/;
export const appointmentKeySchema = z.string().regex(APPOINTMENT_KEY_RE);
export const progressNoteCreateSchema = z.object({
    date: z.coerce.date(),
    sessionLabel: z.string().min(1).max(120),
    format: z.enum(NOTE_FORMATS).default('SOAP'),
    isGroup: z.boolean().default(false),
    body: progressBodySchema.default({}),
    appointmentKey: appointmentKeySchema.optional(),
});
export const progressNoteUpdateSchema = z
    .object({
    date: z.coerce.date().optional(),
    sessionLabel: z.string().min(1).max(120).optional(),
    format: z.enum(NOTE_FORMATS).optional(),
    isGroup: z.boolean().optional(),
    body: progressBodySchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update.' });
// Treatment-plan body: presenting problem + goals → objectives → interventions.
export const interventionSchema = z.object({
    description: z.string().max(2000).default(''),
    frequency: z.string().max(200).default(''),
    format: z.string().max(200).default(''),
    length: z.string().max(200).default(''),
});
export const objectiveSchema = z.object({
    objective_text: z.string().max(4000).default(''),
    progress_status: z.string().max(60).optional(),
    interventions: z.array(interventionSchema).max(50).default([]),
});
export const goalSchema = z.object({
    goal_text: z.string().max(4000).default(''),
    target_date: z.string().max(40).optional(), // ISO 'YYYY-MM-DD' or ''
    responsible_party: z.string().max(200).optional(),
    objectives: z.array(objectiveSchema).max(50).default([]),
});
export const planBodySchema = z.object({
    presenting_problem: z.string().max(20000).optional(),
    goals: z.array(goalSchema).max(50).default([]),
});
export const treatmentPlanCreateSchema = z.object({
    date: z.coerce.date(),
    mode: z.string().min(1).max(60).default('Standard'),
    body: planBodySchema.default({ goals: [] }),
});
export const treatmentPlanUpdateSchema = z
    .object({
    date: z.coerce.date().optional(),
    mode: z.string().min(1).max(60).optional(),
    body: planBodySchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update.' });
// Co-sign workflow, shared by both resources.
// Submit for review: optionally pick the cosigner (else the author's supervisor).
export const clinicalSubmitSchema = z.object({ cosignerId: uuid.optional() });
// Request changes: the reviewer's note to the clinician (calendar#46: stored,
// AES-256-GCM encrypted, in the record's review thread; shown to the author).
export const clinicalChangesSchema = z.object({
    reason: z.string().max(2000).optional(),
    // ehr#277 - progress notes: the reviewer's suggested edits (the full text of each section
    // they changed) and comments, shown to the author as tracked changes.
    suggestions: z
        .object({
        sections: z.record(noteSectionKey, section).default({}),
        comments: z
            .array(noteSuggestionCommentSchema.omit({ at: true }))
            .max(50)
            .default([]),
    })
        .optional(),
    // ehr#571 (reopen 10-27): the reviewer's changes to the note's other fields, and the
    // field-change cards (the whole list as the editor shows it; the server stamps the reviewer's).
    fields: reviewFieldsSchema.optional(),
    fieldChanges: noteFieldChangesSchema.optional(),
    // ehr#741: the reviewer's replies and ticks on the review cards (the server stamps new replies)
    threads: reviewThreadsSchema.optional(),
    // ehr#682: the reviewer's Comment history entries not kept yet (the server stamps them)
    log: reviewLogSchema.optional(),
});
/**
 * ehr#571 (reopen 10-27): Co-sign takes the reviewer's field changes too. ehr#663: and what they
 * typed in the note's boxes (the full text of each box they changed), kept with the co-signature.
 */
export const clinicalCosignSchema = z.object({
    fields: reviewFieldsSchema.optional(),
    fieldChanges: noteFieldChangesSchema.optional(),
    sections: z.record(noteSectionKey, section).optional(),
    threads: reviewThreadsSchema.optional(),
    log: reviewLogSchema.optional(),
});
/**
 * ehr#741 (Chase 10-30, point 4): the reviewer's work on a pending note before they send it back
 * or co-sign: their copy of the boxes, the comments posted, their changes to the other fields, and
 * their replies and ticks. Saved as they go (and with Save Draft) so a closed tab or a reload
 * keeps it; only they read it back; Request changes or Co-sign clears it.
 */
export const reviewDraftSchema = z.object({
    sections: z.record(noteSectionKey, section).default({}),
    comments: z
        .array(z.object({
        id: z.string().max(64),
        section: noteSectionKey,
        text: z.string().trim().min(1).max(2000),
        quote: z.string().max(500).optional(),
        pos: z.number().int().min(0).max(100000).optional(),
        at: z.string().max(40),
    }))
        .max(50)
        .default([]),
    fields: reviewFieldsSchema.optional(),
    threads: reviewThreadsSchema.optional(),
    // ehr#682: the reviewer's Comment history entries so far (the server stamps new ones)
    log: reviewLogSchema.optional(),
});
// calendar#46: the clinician's reply to a "changes requested" note.
export const clinicalReplySchema = z.object({ message: z.string().trim().min(1).max(2000) });
// ehr#544 (PROGRESS_NOTE_10-14-V81 115 A): Add addendum on a signed note. A dated, signed text
// appended by the author, the reviewer or an admin / manager; it never changes the note.
export const ADDENDUM_REASONS = ['Correction', 'Late information', 'Clarification'];
export const noteAddendumCreateSchema = z.object({
    reason: z.enum(ADDENDUM_REASONS),
    text: z.string().trim().min(1, 'Write the addendum first.').max(10000),
});
//# sourceMappingURL=clinical-notes.js.map